import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import AddVehicleType from "../../../Modals/addVehicleType";
import React from "react";
import { useGetCategoriesQuery, useGetVehicleTypesQuery, useDeleteVehicleTypeMutation, useUpdateVehicleTypeMutation } from "../../../service/features/api";
import Pagination from "../../../components/Panigation";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import moment from "moment";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { toast } from "react-toastify";

const ManageVehicleType = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedVehicleType, setSelectedVehicleType] = useState<any>(null);

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100 });

  const { data: vehicleTypesData, isLoading, isFetching } = useGetVehicleTypesQuery({
    page,
    limit,
    search: searchTerm,
    categoryId: category || undefined,
  });

  const [deleteVehicleType, { isLoading: isDeleting }] = useDeleteVehicleTypeMutation();
  const [updateVehicleType] = useUpdateVehicleTypeMutation();

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    setPage(1);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedVehicleType(null);
  };

  const handleEdit = (vehicleType: any) => {
    setSelectedVehicleType(vehicleType);
    setOpenModal(true);
  };

  const handleDeleteClick = (vehicleType: any) => {
    setSelectedVehicleType(vehicleType);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedVehicleType?._id) return;
    try {
      await deleteVehicleType(selectedVehicleType._id).unwrap();
      toast.success("Vehicle Type deleted successfully");
      setOpenDeleteModal(false);
      setSelectedVehicleType(null);
      if (vehicleTypes.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete vehicle type");
    }
  };

  const handleToggle = async (vehicleType: any, field: string, value: boolean) => {
    const payload = {
      id: vehicleType._id,
      name: vehicleType.name,
      categoryId: "6952633da13e6573c358f87b",
      image: vehicleType.image,
      isActive: vehicleType.isActive,
      [field]: value
    };

    try {
      await updateVehicleType(payload).unwrap();
      toast.success("Updated successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  const vehicleTypes = vehicleTypesData?.data?.data || [];
  const paginator = vehicleTypesData?.data?.paginator || {};

  return (
    <>
      <CommonBreadcrumb
        title="Vehicle Type Management"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Listed Vehicles", onClick: () => navigate("/manage-vehicles") },
          { label: "Vehicle Type Management" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(term) => {
                setSearchTerm(term);
                setPage(1);
              }}
              placeholder="Search Vehicle Types..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={category}
                  onChange={handleCategoryChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedVehicleType(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> Add Vehicle Type
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No.</TableCell>
                <TableCell>Vehicle Type Detail</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Disable/Enable</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : vehicleTypes.length > 0 ? (
                vehicleTypes.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(paginator.currentPage - 1) * paginator.perPage + index + 1}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <figure className="no_radius">
                          <img
                            src={row.image || "/static/images/placeholder.png"}
                            alt={row.name}
                            onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                          />
                        </figure>
                        <p>
                          <b>{row.name || "--"}</b>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {Array.isArray(row.categoryName) ? row.categoryName.join(", ") : (row.categoryName || "--")}
                    </TableCell>
                    <TableCell>{moment(row.createdAt).format('DD-MMM-YYYY')}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={(e) => handleToggle(row, "isActive", e.target.checked)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled" : "Disabled"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              onClick={() => handleEdit(row)}
                            >
                              <img src="/static/images/edit2_icon.svg" alt="Edit" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              onClick={() => handleDeleteClick(row)}
                            >
                              <img src="/static/images/trash2_icon.svg" alt="Delete" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No vehicle types found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={vehicleTypes}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
      </div>

      <AddVehicleType
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        vehicleTypeData={selectedVehicleType}
      />

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete the vehicle type "${selectedVehicleType?.name}"?`}
      />
    </>
  );
};

export default ManageVehicleType;
